import React, { useEffect, useState } from 'react';
import config from '../config';

interface CategoryOption {
  id: number;
  name: string;
}

const ManageDocuments: React.FC = () => {
  const [categoryIdsForBook, setCategoryIdsForBook] = useState('');
  const [includeAnswersInBook, setIncludeAnswersInBook] = useState(false);

  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const [selectedExamCategoryIds, setSelectedExamCategoryIds] = useState<number[]>([]);
  const [categoryIdsForExam, setCategoryIdsForExam] = useState('');
  const [includeAnswerKey, setIncludeAnswerKey] = useState(true);
  const [randomizeQuestions, setRandomizeQuestions] = useState(true);
  const [questionsPerCategory, setQuestionsPerCategory] = useState('');
  const [headerText, setHeaderText] = useState('');

  const [examId, setExamId] = useState('');
  const [includeExamAnswerKey, setIncludeExamAnswerKey] = useState(true);

  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategoryOptions = async () => {
      try {
        const response = await fetch(`${config.apiBaseUrl}/api/categories/flat`);
        if (!response.ok) {
          throw new Error('Failed to load categories');
        }

        const data = await response.json();
        const mapped = Array.isArray(data)
          ? data
              .filter((item) => item && typeof item.id === 'number' && typeof item.name === 'string')
              .map((item) => ({ id: item.id as number, name: item.name as string }))
          : [];

        setCategoryOptions(mapped);
      } catch (err) {
        console.error('Failed to load category options:', err);
      }
    };

    fetchCategoryOptions();
  }, []);

  const saveBlobAsFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  const getFileNameFromHeader = (contentDisposition: string | null, fallbackName: string) => {
    if (!contentDisposition) {
      return fallbackName;
    }

    const match = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
    if (!match || !match[1]) {
      return fallbackName;
    }

    const decoded = decodeURIComponent(match[1].replace(/"/g, '').trim());
    return decoded || fallbackName;
  };

  const downloadFromEndpoint = async (url: string, fallbackName: string, actionKey: string) => {
    try {
      setLoadingAction(actionKey);
      setError(null);
      setMessage(null);

      const response = await fetch(url);
      if (!response.ok) {
        const maybeJson = await response.text();
        throw new Error(maybeJson || `Request failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const fileName = getFileNameFromHeader(response.headers.get('content-disposition'), fallbackName);
      saveBlobAsFile(blob, fileName);
      setMessage(`Downloaded ${fileName}`);
    } catch (err: any) {
      setError(err?.message || 'Failed to export file');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleExportBook = async () => {
    const params = new URLSearchParams();
    if (categoryIdsForBook.trim()) {
      params.set('categoryIds', categoryIdsForBook.trim());
    }
    params.set('includeAnswers', String(includeAnswersInBook));

    const url = `${config.apiBaseUrl}/api/Documents/export-book?${params.toString()}`;
    await downloadFromEndpoint(url, `PracticeBook_${Date.now()}.docx`, 'book');
  };

  const handleExportExamByCategories = async () => {
    if (!categoryIdsForExam.trim()) {
      setError('Category IDs are required for category-based exam export.');
      return;
    }

    const params = new URLSearchParams();
    params.set('includeAnswerKey', String(includeAnswerKey));
    params.set('randomizeQuestions', String(randomizeQuestions));

    if (questionsPerCategory.trim()) {
      params.set('questionsPerCategory', questionsPerCategory.trim());
    }

    if (headerText.trim()) {
      params.set('headerText', headerText.trim());
    }

    const encodedCategoryIds = encodeURIComponent(categoryIdsForExam.trim());
    const url = `${config.apiBaseUrl}/api/Documents/export-exam-by-categories/${encodedCategoryIds}?${params.toString()}`;
    await downloadFromEndpoint(url, `CategoryExam_${Date.now()}.pdf`, 'category-exam');
  };

  const parseCategoryIdList = (value: string): number[] => {
    return value
      .split(',')
      .map((part) => Number(part.trim()))
      .filter((id) => Number.isInteger(id) && id > 0);
  };

  const handleExamCategoryMultiSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedIds = Array.from(event.target.selectedOptions)
      .map((option) => Number(option.value))
      .filter((id) => Number.isInteger(id) && id > 0);

    setSelectedExamCategoryIds(selectedIds);
    setCategoryIdsForExam(selectedIds.join(','));
  };

  const handleExamCategoryIdsInputChange = (value: string) => {
    setCategoryIdsForExam(value);

    const parsedIds = parseCategoryIdList(value);
    setSelectedExamCategoryIds(parsedIds);
  };

  const handleExportExamById = async () => {
    if (!examId.trim() || Number.isNaN(Number(examId)) || Number(examId) <= 0) {
      setError('Please enter a valid Exam ID.');
      return;
    }

    const params = new URLSearchParams();
    params.set('includeAnswerKey', String(includeExamAnswerKey));

    const url = `${config.apiBaseUrl}/api/Documents/export-exam/${examId.trim()}?${params.toString()}`;
    await downloadFromEndpoint(url, `Exam_${examId.trim()}_${Date.now()}.pdf`, 'exam-id');
  };

  const sectionStyle: React.CSSProperties = {
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    backgroundColor: '#fff',
    padding: 16,
    marginBottom: 16,
  };

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '24px 16px', textAlign: 'left' }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8, color: '#111827' }}>Manage Documents</h1>
      <p style={{ marginTop: 0, marginBottom: 20, color: '#475569' }}>
        Export practice books and exam PDFs from admin.
      </p>

      {message && (
        <div style={{ marginBottom: 12, padding: 10, borderRadius: 8, backgroundColor: '#dcfce7', color: '#166534' }}>
          {message}
        </div>
      )}

      {error && (
        <div style={{ marginBottom: 12, padding: 10, borderRadius: 8, backgroundColor: '#fee2e2', color: '#991b1b' }}>
          {error}
        </div>
      )}

      <div style={sectionStyle}>
        <h2 style={{ marginTop: 0, marginBottom: 12, fontSize: 18, color: '#0f172a' }}>Export Practice Book (.docx)</h2>
        <input
          type="text"
          value={categoryIdsForBook}
          onChange={(e) => setCategoryIdsForBook(e.target.value)}
          placeholder="Category IDs (optional), e.g. 10,11,12"
          style={{ width: '100%', marginBottom: 10, padding: 10, borderRadius: 6, border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
        />

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12, color: '#334155' }}>
          <input
            type="checkbox"
            checked={includeAnswersInBook}
            onChange={(e) => setIncludeAnswersInBook(e.target.checked)}
          />
          Include answers in exported book
        </label>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={handleExportBook}
            disabled={loadingAction !== null}
            style={{
              backgroundColor: '#2563eb',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '10px 14px',
              cursor: loadingAction ? 'not-allowed' : 'pointer',
              fontWeight: 600,
            }}
          >
            {loadingAction === 'book' ? 'Exporting...' : 'Export Practice Book'}
          </button>
          <span style={{ color: '#475569', fontSize: 14 }}>
            Generates a Word document from selected categories, optionally including answers.
          </span>
        </div>
      </div>

      <div style={sectionStyle}>
        <h2 style={{ marginTop: 0, marginBottom: 12, fontSize: 18, color: '#0f172a' }}>Export Exam by Categories (.pdf)</h2>
        <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#334155' }}>
          Select categories (multi-select)
        </label>
        <select
          multiple
          value={selectedExamCategoryIds.map(String)}
          onChange={handleExamCategoryMultiSelectChange}
          style={{
            width: '100%',
            minHeight: 160,
            marginBottom: 10,
            padding: 8,
            borderRadius: 6,
            border: '1px solid #cbd5e1',
            backgroundColor: '#fff',
            boxSizing: 'border-box',
          }}
        >
          {categoryOptions.map((category) => (
            <option key={category.id} value={category.id}>
              {category.id} - {category.name}
            </option>
          ))}
        </select>

        <input
          type="text"
          value={categoryIdsForExam}
          onChange={(e) => handleExamCategoryIdsInputChange(e.target.value)}
          placeholder="Category IDs (auto-filled from selection), e.g. 10,11,12"
          style={{ width: '100%', marginBottom: 10, padding: 10, borderRadius: 6, border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
        />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 10, marginBottom: 10 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#334155' }}>
            <input
              type="checkbox"
              checked={includeAnswerKey}
              onChange={(e) => setIncludeAnswerKey(e.target.checked)}
            />
            Include answer key
          </label>

          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#334155' }}>
            <input
              type="checkbox"
              checked={randomizeQuestions}
              onChange={(e) => setRandomizeQuestions(e.target.checked)}
            />
            Randomize questions
          </label>

          <input
            type="number"
            min={1}
            value={questionsPerCategory}
            onChange={(e) => setQuestionsPerCategory(e.target.value)}
            placeholder="Questions per category"
            style={{ padding: 10, borderRadius: 6, border: '1px solid #cbd5e1' }}
          />

          <input
            type="text"
            value={headerText}
            onChange={(e) => setHeaderText(e.target.value)}
            placeholder="Header text (optional)"
            style={{ padding: 10, borderRadius: 6, border: '1px solid #cbd5e1' }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={handleExportExamByCategories}
            disabled={loadingAction !== null}
            style={{
              backgroundColor: '#0d9488',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '10px 14px',
              cursor: loadingAction ? 'not-allowed' : 'pointer',
              fontWeight: 600,
            }}
          >
            {loadingAction === 'category-exam' ? 'Exporting...' : 'Export Category Exam'}
          </button>
          <span style={{ color: '#475569', fontSize: 14 }}>
            Generates a PDF exam using comma-separated category IDs with optional randomization and limits.
          </span>
        </div>
      </div>

      <div style={sectionStyle}>
        <h2 style={{ marginTop: 0, marginBottom: 12, fontSize: 18, color: '#0f172a' }}>Export Existing Exam by ID (.pdf)</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 10, marginBottom: 10 }}>
          <input
            type="number"
            min={1}
            value={examId}
            onChange={(e) => setExamId(e.target.value)}
            placeholder="Exam ID"
            style={{ padding: 10, borderRadius: 6, border: '1px solid #cbd5e1' }}
          />

          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#334155' }}>
            <input
              type="checkbox"
              checked={includeExamAnswerKey}
              onChange={(e) => setIncludeExamAnswerKey(e.target.checked)}
            />
            Include answer key
          </label>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={handleExportExamById}
            disabled={loadingAction !== null}
            style={{
              backgroundColor: '#7c3aed',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '10px 14px',
              cursor: loadingAction ? 'not-allowed' : 'pointer',
              fontWeight: 600,
            }}
          >
            {loadingAction === 'exam-id' ? 'Exporting...' : 'Export Exam by ID'}
          </button>
          <span style={{ color: '#475569', fontSize: 14 }}>
            Downloads a PDF for a specific exam record.
          </span>
        </div>
      </div>
    </div>
  );
};

export default ManageDocuments;
